import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Switch,
  TextField,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useTheme } from '@mui/material/styles';
import api, { TYLER_SCAN, TRADE } from '../../infrastructure/config/api';
import { useSnackbar } from 'notistack';

const DEFAULT_TYLER_USERS = [
  { username: 'khanh', label: 'Khanh' },
  { username: 'bien', label: 'Biên' },
  { username: 'devk', label: 'Devk' },
  { username: 'phu', label: 'Phú' },
];

function normalizeClientSettings(payload = {}) {
  const fallbackNotional = Number(payload.notionalUsdt) > 0 ? Number(payload.notionalUsdt) : 50;
  const incomingUsers = Array.isArray(payload.users) ? payload.users : [];
  return {
    notionalUsdt: fallbackNotional,
    users: DEFAULT_TYLER_USERS.map(({ username }) => {
      const match = incomingUsers.find((item) => String(item?.username || '').toLowerCase() === username);
      return {
        username,
        enabled: match?.enabled !== false,
        notionalUsdt: Number(match?.notionalUsdt) > 0 ? Number(match.notionalUsdt) : fallbackNotional,
      };
    }),
  };
}

function formatDateTime(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleString();
}

function formatCountdown(value, nowMs = Date.now()) {
  if (!value) {
    return '-';
  }

  const targetTime = new Date(value).getTime();
  if (Number.isNaN(targetTime)) {
    return '-';
  }

  const diffMs = Math.max(0, targetTime - nowMs);
  const totalSeconds = Math.ceil(diffMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

function formatTelegramStatus(telegram) {
  if (!telegram) {
    return 'Telegram -';
  }

  if (telegram.skipped) {
    return telegram.reason === 'no_signal' ? 'Telegram no signal' : `Telegram ${telegram.reason}`;
  }

  const sentCount = Array.isArray(telegram.sent) ? telegram.sent.length : 0;
  const failedCount = Array.isArray(telegram.failed) ? telegram.failed.length : 0;
  if (failedCount > 0) {
    return `Telegram sent ${sentCount}, failed ${failedCount}`;
  }

  return `Telegram sent ${sentCount}`;
}

function formatMarketCategories(categories) {
  if (!Array.isArray(categories) || categories.length === 0) {
    return '-';
  }

  return categories.join(', ');
}

function sortRowsByTriggerTimeDesc(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  return [...rows].sort((a, b) => {
    const timeA = new Date(a?.triggerCandleTime).getTime();
    const timeB = new Date(b?.triggerCandleTime).getTime();
    const safeTimeA = Number.isNaN(timeA) ? Number.MAX_SAFE_INTEGER : timeA;
    const safeTimeB = Number.isNaN(timeB) ? Number.MAX_SAFE_INTEGER : timeB;

    if (safeTimeA !== safeTimeB) {
      return safeTimeB - safeTimeA;
    }

    return (b?.score || 0) - (a?.score || 0);
  });
}

function getScanErrorMessage(error, fallbackMessage) {
  const status = error?.response?.status;
  const code = error?.code;
  if (status === 504 || code === 'ECONNABORTED') {
    return 'Tyler scan đang bận hoặc quá thời gian xử lý, vui lòng thử lại.';
  }
  return error?.response?.data?.error || fallbackMessage;
}

function mapScanData(payload = {}) {
  return {
    timeframe: payload.timeframe || '30m',
    generatedAt: payload.generatedAt || null,
    currentTimeVn: payload.currentTimeVn || null,
    scannedSymbols: payload.scannedSymbols || 0,
    totalSymbols: payload.totalSymbols || 0,
    skippedSymbols: payload.skippedSymbols || 0,
    topBuy: payload.topBuy || [],
    topSell: payload.topSell || [],
    scan: payload.scan || null,
    telegram: payload.telegram || null,
  };
}

function MobileSignalCard({ row, onEnter, enterDisabled, enterLoading }) {
  return (
    <Paper
      sx={{
        p: 1.5,
        background: '#121826',
        border: '1px solid #27304a',
        borderRadius: 2,
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="body1" fontWeight={700}>
            {row.symbol}
          </Typography>
          <Chip
            label={row.side}
            color={row.side === 'BUY' ? 'success' : 'error'}
            size="small"
          />
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`Score ${row.score}`} size="small" variant="outlined" />
          <Chip label={`Age ${row.ageBars}`} size="small" variant="outlined" />
        </Stack>

        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Group: {formatMarketCategories(row.marketCategories)}
          </Typography>
          <Typography variant="caption" color="text.secondary">Entry: {row.entryPrice}</Typography>
          <Typography variant="caption" color="text.secondary">
            Exit: Dashboard ROE Guard
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Trigger: {formatDateTime(row.triggerCandleTime)}
          </Typography>
        </Stack>

        <Divider sx={{ borderColor: '#27304a' }} />

        <Stack spacing={0.5}>
          {(row.reason || []).map((item) => (
            <Typography key={item} variant="caption" color="text.secondary">
              {item}
            </Typography>
          ))}
        </Stack>

        <Button
          variant="contained"
          color={row.side === 'BUY' ? 'success' : 'error'}
          onClick={() => onEnter?.(row)}
          disabled={enterDisabled}
          startIcon={enterLoading ? <CircularProgress size={16} color="inherit" /> : null}
          fullWidth
        >
          {enterLoading ? 'Đang vào lệnh...' : 'Vào lệnh'}
        </Button>
      </Stack>
    </Paper>
  );
}

function SignalTable({ title, side, rows, mobile, onEnter, enterLoadingKey }) {
  const sortedRows = sortRowsByTriggerTimeDesc(rows);

  return (
    <Paper
      sx={{
        background: '#171c29',
        border: '1px solid #27304a',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid #27304a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6">{title}</Typography>
        <Chip
          label={`${sortedRows.length} results`}
          color={side === 'BUY' ? 'success' : 'error'}
          variant="outlined"
          size="small"
        />
      </Box>

      {mobile ? (
        <Stack spacing={1.25} sx={{ p: 1.25 }}>
          {sortedRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
              No signal found.
            </Typography>
          ) : (
            sortedRows.map((row) => (
              <MobileSignalCard
                key={`${row.symbol}-${row.triggerCandleTime}-${row.side}`}
                row={row}
                onEnter={onEnter}
                enterDisabled={!!enterLoadingKey}
                enterLoading={enterLoadingKey === `${row.symbol}-${row.triggerCandleTime}-${row.side}`}
              />
            ))
          )}
        </Stack>
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell>Symbol</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Age</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Entry</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No signal found.
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows.map((row) => (
                  <TableRow key={`${row.symbol}-${row.triggerCandleTime}-${row.side}`}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={700}>
                          {row.symbol}
                        </Typography>
                        <Chip
                          label={row.side}
                          color={row.side === 'BUY' ? 'success' : 'error'}
                          size="small"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>{row.score}</TableCell>
                    <TableCell>{row.ageBars}</TableCell>
                    <TableCell>{formatMarketCategories(row.marketCategories)}</TableCell>
                    <TableCell>{row.entryPrice}</TableCell>
                    <TableCell>{formatDateTime(row.triggerCandleTime)}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        {(row.reason || []).map((item) => (
                          <Typography key={item} variant="caption" color="text.secondary">
                            {item}
                          </Typography>
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="contained"
                        size="small"
                        color={row.side === 'BUY' ? 'success' : 'error'}
                        onClick={() => onEnter?.(row)}
                        disabled={!!enterLoadingKey}
                        startIcon={
                          enterLoadingKey === `${row.symbol}-${row.triggerCandleTime}-${row.side}`
                            ? <CircularProgress size={14} color="inherit" />
                            : null
                        }
                      >
                        {enterLoadingKey === `${row.symbol}-${row.triggerCandleTime}-${row.side}`
                          ? '...'
                          : 'Vào lệnh'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

const TylerScanPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [data, setData] = useState({
    timeframe: '30m',
    generatedAt: null,
    scannedSymbols: 0,
    topBuy: [],
    topSell: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [confirmRow, setConfirmRow] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [clientSettings, setClientSettings] = useState(() => normalizeClientSettings({ notionalUsdt: 50 }));
  const [enterLoadingKey, setEnterLoadingKey] = useState('');
  const [scanError, setScanError] = useState('');
  const [nowMs, setNowMs] = useState(Date.now());

  const loadClientSettings = useCallback(async () => {
    try {
      setSettingsLoading(true);
      const response = await api.get(TYLER_SCAN.SETTINGS);
      const payload = response.data?.data || {};
      setClientSettings(normalizeClientSettings(payload));
    } catch (error) {
      console.error('Failed to load Tyler settings:', error);
      enqueueSnackbar(error.response?.data?.error || 'Failed to load Tyler settings', { variant: 'error' });
    } finally {
      setSettingsLoading(false);
    }
  }, [enqueueSnackbar]);

  const loadLatest = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.get(TYLER_SCAN.LATEST);
      const payload = response.data?.data || {};
      setScanError('');
      setData(mapScanData(payload));
      setIsScanning(payload.scan?.status === 'running');
    } catch (error) {
      console.error('Failed to load Tyler scan results:', error);
      const message = getScanErrorMessage(error, 'Failed to load scan results');
      setScanError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar]);

  const runScan = useCallback(async () => {
    try {
      setIsScanning(true);
      const response = await api.post(TYLER_SCAN.RUN);
      setScanError('');
      enqueueSnackbar(
        response.data?.accepted ? 'Scan đã bắt đầu chạy nền' : 'Scan đang chạy',
        { variant: 'info' }
      );
    } catch (error) {
      console.error('Failed to run Tyler scan:', error);
      const message = getScanErrorMessage(error, 'Failed to run scan');
      setScanError(message);
      setIsScanning(false);
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadLatest();
    loadClientSettings();
  }, [loadLatest, loadClientSettings]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      loadLatest();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [loadLatest]);

  const openSettings = useCallback(() => {
    setSettingsOpen(true);
    if (!clientSettings?.users?.length) {
      loadClientSettings();
    }
  }, [clientSettings?.users?.length, loadClientSettings]);

  const closeSettings = useCallback(() => {
    if (settingsSaving) return;
    setSettingsOpen(false);
  }, [settingsSaving]);

  const updateUserSetting = useCallback((username, patch) => {
    setClientSettings((current) => ({
      ...current,
      users: normalizeClientSettings(current).users.map((item) => (
        item.username === username ? { ...item, ...patch } : item
      )),
    }));
  }, []);

  const saveSettings = useCallback(async () => {
    const users = normalizeClientSettings(clientSettings).users;
    const invalidUser = users.find((item) => !Number.isFinite(Number(item.notionalUsdt)) || Number(item.notionalUsdt) <= 0);
    if (invalidUser) {
      enqueueSnackbar(`Notional của ${invalidUser.username} phải là số dương`, { variant: 'error' });
      return;
    }

    try {
      setSettingsSaving(true);
      const response = await api.post(TYLER_SCAN.SETTINGS, { users });
      setClientSettings(normalizeClientSettings(response.data?.data || { users }));
      enqueueSnackbar('Đã lưu cài đặt Tyler scan', { variant: 'success' });
      setSettingsOpen(false);
    } catch (error) {
      console.error('Failed to save Tyler settings:', error);
      enqueueSnackbar(error.response?.data?.error || 'Failed to save Tyler settings', { variant: 'error' });
    } finally {
      setSettingsSaving(false);
    }
  }, [clientSettings, enqueueSnackbar]);

  useEffect(() => {
    if (!isScanning) return undefined;

    const intervalId = setInterval(async () => {
      try {
        const response = await api.get(TYLER_SCAN.LATEST);
        const payload = response.data?.data || {};
        const status = payload.scan?.status;

        if (status === 'running') return;

        setData(mapScanData(payload));
        setIsScanning(false);
        if (status === 'completed') {
          setScanError('');
          enqueueSnackbar('Scan completed', { variant: 'success' });
        } else if (status === 'failed') {
          const message = payload.scan?.error || 'Failed to run scan';
          setScanError(message);
          enqueueSnackbar(message, { variant: 'error' });
        }
      } catch (error) {
        console.error('Failed to poll Tyler scan:', error);
      }
    }, 2000);

    return () => clearInterval(intervalId);
  }, [enqueueSnackbar, isScanning]);

  const handleEnterClick = useCallback((row) => {
    setConfirmRow(row);
  }, []);

  const handleCloseConfirm = useCallback(() => {
    if (enterLoadingKey) return;
    setConfirmRow(null);
  }, [enterLoadingKey]);

  const handleConfirmEnter = useCallback(async () => {
    if (!confirmRow) return;

    const key = `${confirmRow.symbol}-${confirmRow.triggerCandleTime}-${confirmRow.side}`;

    try {
      setEnterLoadingKey(key);
      const payload = {
        symbol: confirmRow.symbol,
        side: confirmRow.side,
        entryPrice: confirmRow.entryPrice,
        investment: 10,
        leverage: 10,
        marginMode: 'CROSSED',
        confirmAction: 'MANUAL_ENTRY',
        confirmSymbol: confirmRow.symbol,
        confirmSide: confirmRow.side,
      };

      const response = await api.post(TRADE.MANUAL_ENTRY, payload);
      const first = response.data?.data?.[0];
      const meta = first
        ? ` | margin ${first.investment} | lev ${first.leverage}x | ${first.marginMode}`
        : '';
      enqueueSnackbar((response.data?.message || 'Đã vào lệnh') + meta, { variant: 'success' });
      setConfirmRow(null);
    } catch (error) {
      console.error('Failed to enter trade:', error);
      enqueueSnackbar(error.response?.data?.error || error.response?.data?.message || 'Vào lệnh thất bại', { variant: 'error' });
    } finally {
      setEnterLoadingKey('');
    }
  }, [confirmRow, enqueueSnackbar]);

  const autoScan = data.scan?.autoScan || {};
  const signalCount = (data.topBuy?.length || 0) + (data.topSell?.length || 0);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, md: 3 } }}>
      <Stack spacing={3}>
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            background: 'linear-gradient(135deg, #111827 0%, #182235 100%)',
            border: '1px solid #27304a',
            borderRadius: 2,
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700}>
                Tyler 30m Scanner
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Binance Futures USDT-M {data.timeframe} | MA5/9/13/200 + SVG/FVG retest checklist
              </Typography>
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              <Chip label={`Scanned ${data.scannedSymbols} symbols`} variant="outlined" />
              <Chip
                label={`Signals ${signalCount}`}
                color={signalCount > 0 ? 'success' : 'default'}
                variant={signalCount > 0 ? 'filled' : 'outlined'}
              />
              {data.totalSymbols ? (
                <Chip
                  label={`Skipped ${data.skippedSymbols || 0}/${data.totalSymbols}`}
                  variant="outlined"
                />
              ) : null}
              <Chip
                label={`Updated ${formatDateTime(data.generatedAt)}`}
                variant="outlined"
                sx={{ maxWidth: { xs: '100%', sm: 'none' } }}
              />
              <Button
                variant="contained"
                startIcon={isScanning ? <CircularProgress size={16} color="inherit" /> : <AutorenewIcon />}
                onClick={runScan}
                disabled={isScanning}
                fullWidth={isMobile}
              >
                {isScanning ? 'Scanning...' : 'Scan'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<SettingsOutlinedIcon />}
                onClick={openSettings}
                fullWidth={isMobile}
              >
                Cài đặt
              </Button>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            <Chip
              label={autoScan.enabled ? 'Auto scan ON' : 'Auto scan OFF'}
              color={autoScan.enabled ? 'success' : 'default'}
              variant={autoScan.enabled ? 'filled' : 'outlined'}
            />
            <Chip
              label={`Next auto ${formatCountdown(autoScan.nextRunAt, nowMs)}`}
              variant="outlined"
            />
            <Chip
              label={`Last ${data.scan?.source || '-'} ${formatDateTime(data.scan?.finishedAt || data.generatedAt)}`}
              variant="outlined"
            />
            <Chip
              label={formatTelegramStatus(data.telegram)}
              color={data.telegram && !data.telegram.skipped ? 'success' : 'default'}
              variant="outlined"
            />
          </Stack>
        </Paper>
        {scanError ? (
          <Alert
            severity="warning"
            action={(
              <Button
                color="inherit"
                size="small"
                onClick={loadLatest}
                disabled={isLoading || isScanning}
              >
                Thử lại
              </Button>
            )}
          >
            {scanError}
          </Alert>
        ) : null}

        {isLoading ? (
          <Box sx={{ py: 12, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            <Paper
              sx={{
                p: 2,
                background: '#151a26',
                border: '1px solid #27304a',
                borderRadius: 2,
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Client setting
                  </Typography>
                  <Typography variant="body1" fontWeight={700}>
                    {normalizeClientSettings(clientSettings).users
                      .map((item) => (item.enabled
                        ? `${item.username}: ${item.notionalUsdt} USDT`
                        : `${item.username}: OFF`))
                      .join(' | ')}
                  </Typography>
                </Box>
                <Button variant="text" onClick={openSettings}>
                  Chỉnh sửa
                </Button>
              </Stack>
            </Paper>
            <SignalTable
              title="BUY Signals"
              side="BUY"
              rows={data.topBuy}
              mobile={isMobile}
              onEnter={handleEnterClick}
              enterLoadingKey={enterLoadingKey}
            />
            <SignalTable
              title="SELL Signals"
              side="SELL"
              rows={data.topSell}
              mobile={isMobile}
              onEnter={handleEnterClick}
              enterLoadingKey={enterLoadingKey}
            />
          </Stack>
        )}
      </Stack>

      <Dialog open={!!confirmRow} onClose={handleCloseConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>Vào lệnh {confirmRow?.side}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Typography variant="body2">
              Symbol: <strong>{confirmRow?.symbol}</strong>
            </Typography>
            <Typography variant="body2">Entry: {confirmRow?.entryPrice}</Typography>
            <Typography variant="body2">Exit: Dashboard ROE Guard (+40% mặc định)</Typography>
            <Divider sx={{ borderColor: '#27304a' }} />
            <Typography variant="caption" color="text.secondary">
              Default: Margin 10 USDT • Leverage 10x • Mode Cross (CROSSED)
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} disabled={!!enterLoadingKey}>
            Huỷ
          </Button>
          <Button
            variant="contained"
            color={confirmRow?.side === 'BUY' ? 'success' : 'error'}
            onClick={handleConfirmEnter}
            disabled={!!enterLoadingKey}
            startIcon={enterLoadingKey ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {enterLoadingKey ? 'Đang gửi...' : 'Xác nhận'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={settingsOpen} onClose={closeSettings} maxWidth="xs" fullWidth>
        <DialogTitle>Cài đặt Tyler scan</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Bật/tắt từng tài khoản và đặt vol cho auto entry Tyler scan. Tài khoản OFF sẽ
              không tự vào lệnh Binance khi scan; mặc định tất cả tài khoản đều ON.
            </Typography>
            {normalizeClientSettings(clientSettings).users.map((item) => {
              const label = DEFAULT_TYLER_USERS.find((user) => user.username === item.username)?.label
                || item.username;
              return (
                <Box
                  key={item.username}
                  sx={{
                    border: '1px solid #27304a',
                    borderRadius: 2,
                    p: 1.5,
                    opacity: item.enabled ? 1 : 0.6,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body1" fontWeight={700}>
                        {label}
                      </Typography>
                      <Chip
                        size="small"
                        label={item.enabled ? 'ON' : 'OFF'}
                        color={item.enabled ? 'success' : 'default'}
                        variant={item.enabled ? 'filled' : 'outlined'}
                      />
                    </Stack>
                    <Switch
                      checked={item.enabled}
                      onChange={(event) => updateUserSetting(item.username, { enabled: event.target.checked })}
                      disabled={settingsLoading || settingsSaving}
                      color="success"
                    />
                  </Stack>
                  <TextField
                    label="Notional USDT"
                    type="number"
                    value={item.notionalUsdt || ''}
                    onChange={(event) => updateUserSetting(item.username, { notionalUsdt: event.target.value })}
                    inputProps={{ min: 1, step: '1' }}
                    disabled={settingsLoading || settingsSaving || !item.enabled}
                    fullWidth
                    size="small"
                    sx={{ mt: 1.5 }}
                  />
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSettings} disabled={settingsSaving}>
            Huỷ
          </Button>
          <Button
            variant="contained"
            onClick={saveSettings}
            disabled={settingsLoading || settingsSaving}
            startIcon={settingsSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {settingsSaving ? 'Đang lưu...' : 'Lưu'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TylerScanPage;
